import { v4 as uuid } from 'uuid';
import { getPublicEnv } from '@UI/components/PublicEnv/public-env';

import type { SystemCurrencyConfig } from '@controls/store/system';
import { systemFixedCurrency } from '@controls/store/system';
import type { TFunction } from 'i18next';
import { moneyAmountFormat } from '@shared/@controls/constants';
import { dayjsGlobal } from '@shared/@controls/utils';
import { CivilityEnum } from '@controls/types';

export const superAdminEmail = getPublicEnv('SUPER_ADMIN_EMAIL');
export const PROJECT_NAME = getPublicEnv('PROJECT_TITLE');

export function autoGenEmail() {
  return `dlm${uuid()}@gmail.com`;
}

export function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const getRequiredMessage = (t: TFunction | any, key: string) => {
  return t('common:field_required', { field: t(`common:${key}`) }).toString();
};

export const getRequiredMessageSelectField = (t: TFunction | any, key: string) => {
  return t('common:must_be_select', { field: t(`common:${key}`).toLowerCase() }).toString();
};

export const getMinMessage = (t: TFunction | any, key: string, min: number) => {
  return t('common:greater_than', {
    type: t(`common:${key}`),
    number: min,
  }).toString();
};

export const getNameFile = (publicUrl: string) => {
  const fileName = publicUrl.split('/').pop();
  return fileName ?? '';
};

export const downloadFileWithUrl = (url: string) => {
  const link = document.createElement('a');
  link.href = `${getPublicEnv('PUBLIC_URL_DOWNLOAD')}${url}`;
  link.download = getNameFile(url ?? '');
  link.setAttribute('target', '_blank');
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const formatCurrency = (
  value?: string | number,
  config: SystemCurrencyConfig = systemFixedCurrency,
) => {
  if (!value && typeof value !== 'number') {
    return '';
  }
  return parseFloat(value.toString()).toLocaleString(config.locale, {
    style: 'currency',
    currency: config.code,
  });
};

export const getCurrency = () => systemFixedCurrency.symbol;

export const formatNumber = (value?: string | number, format: RegExp = moneyAmountFormat) => {
  // format number value to this format 1,000,000.123 by using Regex only
  if (!value && typeof value !== 'number') {
    return '';
  }
  return value.toString().replace(format, ',');
};

export function uppercaseFirstString(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function removeEmptyObjects(obj: Record<string, any>) {
  return Object.keys(obj).reduce((acc: any, key) => {
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      const nestedObj = removeEmptyObjects(obj[key]);
      if (Object.keys(nestedObj).length !== 0) {
        acc[key] = nestedObj;
      }
    } else {
      acc[key] = obj[key];
    }
    return acc;
  }, {});
}

export function removeValueFromObj(object: Record<string, any>) {
  Object.entries(object).forEach(([k, v]) => {
    if (v && typeof v === 'object') {
      removeValueFromObj(v);
    }
    if (
      (v && typeof v === 'object' && !Object.keys(v).length) ||
      v === null ||
      v === undefined ||
      v.length === 0
    ) {
      if (Array.isArray(object)) {
        object.splice(Number(k), 1);
      } else if (!(v instanceof Date)) {
        delete object[k];
      }
    }
  });
  return object;
}

export const delayWithoutPromise = (time: number) => {
  const now = dayjsGlobal().valueOf();

  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (dayjsGlobal().valueOf() > now + time) {
      break;
    }
  }
};

export const giveValueIf = (cond: boolean, value: any) => (cond ? value : undefined);
export const valueOrNull = (value: any) => (!!value ? value : null);
export const valueOrUndefined = (value: any) => (value !== undefined ? value : undefined);
export const eliminateTypeName = (obj: any) => {
  if (obj && typeof obj === 'object') {
    const { __typename, ...rest } = obj;
    return Object.keys(rest).reduce(
      (acc, key) => {
        acc[key] = eliminateTypeName(rest[key]);
        return acc;
      },
      {} as typeof obj,
    );
  }
  return obj;
};

export const concatenationToLink = (link: string) => {
  // Fixme: Must get server url from env : correct is getPublicEnv('GRAPHQL_ENDPOINT')
  return `${getPublicEnv('SERVER_API')}${link}`;
};

export const getFullName = (
  value?: any & { civility: string; firstName?: string; lastName?: string },
) => {
  return value
    ? ` ${value.civility || CivilityEnum.Mr}. ${valueOrNull(value.firstName)} ${valueOrNull(value.lastName)}`
    : '';
};

export function randomStep(min: number, max: number, step = 1): number {
  if (step <= 0) throw new Error('step must be > 0');
  if (isNaN(min) || isNaN(max) || isNaN(step)) throw new Error('min, max and step must be numbers');

  // Ensure min <= max
  if (min > max) [min, max] = [max, min];

  const getDecimalPlaces = (n: number) => {
    const s = String(n);
    if (s.includes('e-')) {
      // handle scientific notation like 1e-7
      const [, exp] = s.split('e-');
      return parseInt(exp, 10);
    }
    return (s.split('.')[1] || '').length;
  };

  const decimals = Math.max(getDecimalPlaces(min), getDecimalPlaces(max), getDecimalPlaces(step));

  // Number of whole steps that fit in the interval (inclusive of min)
  const totalSteps = Math.floor((max - min) / step);
  // Pick a random integer index between 0 and totalSteps (inclusive)
  const idx = Math.floor(Math.random() * (totalSteps + 1));
  const raw = min + idx * step;

  // Round to avoid floating point precision issues
  const factor = Math.pow(10, decimals);
  return Math.round(raw * factor) / factor;
}
